// ─── Display Modes & Enums ───────────────────────────────────────────────────

export type DisplayMode = 'totals' | 'per100' | 'rates' | 'custom';

export type CategoryTab = 'stokastic' | 'props' | 'shooting' | 'shotZones' | 'rebounding' | 'defense' | 'playmaking' | 'custom';
export type RateMode = 'totals' | 'per100' | 'per36' | 'per1';
export type FantasyPlatform = 'dk' | 'fd';

export type Condition = 'OnFloor' | 'OffFloor' | 'Started' | 'CameOffBench' | 'PlayedInGame';

export type Comparison = 'AtLeast' | 'Exactly' | 'AtMost';

// ─── Filter Types ────────────────────────────────────────────────────────────

export interface FilterPool {
  id: string;
  playerIds: string[];
  condition: Condition;
  comparison: Comparison;
  count: number;
}

export type HomeAway = 'home' | 'away' | null;

export interface OptionalFilters {
  leverage: string[];
  periods: number[];
  fromMargin: number | null;
  toMargin: number | null;
  fromDate: string | null;
  toDate: string | null;
  opponent: string | null;
  homeAway: HomeAway;
}

export const DEFAULT_OPTIONAL_FILTERS: OptionalFilters = {
  leverage: [],
  periods: [],
  fromMargin: null,
  toMargin: null,
  fromDate: null,
  toDate: null,
  opponent: null,
  homeAway: null,
};

export const HOME_AWAY_OPTIONS: { value: HomeAway; label: string }[] = [
  { value: null, label: 'All Games' },
  { value: 'home', label: 'Home' },
  { value: 'away', label: 'Away' },
];

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

// ─── API Response Types ──────────────────────────────────────────────────────

export interface NbaRatesPlayer {
  player_id: string;
  player_name: string;
  team_id: string;
}

export interface NbaRatesTeamPlayersResponse {
  team_id: string;
  season: string;
  players: NbaRatesPlayer[];
}

export interface NbaRatesAggregateStats {
  minutes: number;
  possessions: number;
  off_poss: number;
  def_poss: number;
  off_rtg: number | null;
  def_rtg: number | null;
  net_rtg: number | null;
  fg2_pct: number | null;
  fg3_pct: number | null;
  efg_pct: number | null;
  ts_pct: number | null;
  usage: number | null;
}

export interface NbaRatesPlayerStats {
  player_id: string;
  player_name: string;
  minutes: number;
  possessions: number;
  off_poss: number;
  def_poss: number;
  // Counting stats
  pts: number;
  reb: number;
  ast: number;
  stl: number;
  blk: number;
  tov: number;
  oreb: number;
  dreb: number;
  // Shooting counts
  fg2a: number;
  fg2m: number;
  fg3a: number;
  fg3m: number;
  fta: number;
  ftm: number;
  // Shooting percentages
  fg2_pct: number | null;
  fg3_pct: number | null;
  ft_pct: number | null;
  efg_pct: number | null;
  ts_pct: number | null;
  fg3a_pct: number | null;
  // Usage and efficiency
  usage: number | null;
  off_rtg: number | null;
  def_rtg: number | null;
  net_rtg: number | null;
  // Playmaking and rebounding rates
  ast_ratio: number | null;
  tov_pct: number | null;
  off_reb_pct: number | null;
  def_reb_pct: number | null;
  // ─── Extended Fields ────────────────────────────────────────────────────
  // Totals
  fga: number;
  fgm: number;
  fg_pct: number | null;
  // General
  plus_minus: number;
  // Efficiency
  ppp: number | null;
  ftr: number | null;
  shot_quality_avg: number | null;
  // Shot location - At Rim
  at_rim_fga: number;
  at_rim_fgm: number;
  at_rim_pct: number | null;
  at_rim_freq: number | null;
  at_rim_ast_pct: number | null;
  at_rim_blk_pct: number | null;
  // Shot location - Short Mid Range
  short_mid_fga: number;
  short_mid_fgm: number;
  short_mid_pct: number | null;
  short_mid_freq: number | null;
  // Shot location - Long Mid Range
  long_mid_fga: number;
  long_mid_fgm: number;
  long_mid_pct: number | null;
  long_mid_freq: number | null;
  // Shot location - Corner 3
  corner3_fga: number;
  corner3_fgm: number;
  corner3_pct: number | null;
  corner3_freq: number | null;
  // Shot location - Arc (Above Break) 3
  arc3_fga: number;
  arc3_fgm: number;
  arc3_pct: number | null;
  arc3_freq: number | null;
  // Playmaking (extended)
  assist_points: number;
  two_pt_assists: number;
  three_pt_assists: number;
  assisted_2s_pct: number | null;
  assisted_3s_pct: number | null;
  ast_tov_ratio: number | null;
  // Fouls
  fouls: number;
  fouls_drawn: number;
  shooting_fouls_drawn_pct: number | null;
  and1_trips: number;
  loose_ball_fouls_drawn: number;
  offensive_fouls: number;
  // Defense (extended)
  blocks_recovered_pct: number | null;
  recovered_blocks: number;
  // Turnovers (detailed)
  live_ball_tov_pct: number | null;
  bad_pass_tov: number;
  lost_ball_tov: number;
  // Second Chance
  second_chance_pts: number;
  // Rebounding (extended)
  self_oreb_pct: number | null;
}

export interface NbaRatesStatsResponse {
  aggregate: NbaRatesAggregateStats;
  players: NbaRatesPlayerStats[];
  query_params: Record<string, string>;
  fetched_at: string;
}

/** When includeBaseline=true, the API returns both filtered and baseline data */
export interface NbaRatesStatsWithBaselineResponse {
  filtered: NbaRatesStatsResponse;
  baseline: NbaRatesStatsResponse;
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const SEASONS = [
  { value: '2025-26', label: '2025-26' },
  { value: '2024-25', label: '2024-25' },
  { value: '2023-24', label: '2023-24' },
  { value: '2022-23', label: '2022-23' },
  { value: '2021-22', label: '2021-22' },
  { value: '2020-21', label: '2020-21' },
  { value: '2019-20', label: '2019-20' },
  { value: '2018-19', label: '2018-19' },
];

export const SEASON_TYPES = [
  { value: 'Regular Season', label: 'Regular Season' },
  { value: 'Playoffs', label: 'Playoffs' },
];

export const CONDITIONS: { value: Condition; label: string; color: string }[] = [
  { value: 'OnFloor', label: 'On', color: '#22c55e' },
  { value: 'OffFloor', label: 'Off', color: '#ef4444' },
  { value: 'Started', label: 'Started', color: '#a855f7' },
  { value: 'CameOffBench', label: 'Bench', color: '#f97316' },
  { value: 'PlayedInGame', label: 'Played', color: '#3b82f6' },
];

export const COMPARISONS: { value: Comparison; symbol: string }[] = [
  { value: 'AtLeast', symbol: '\u2265' },
  { value: 'Exactly', symbol: '=' },
  { value: 'AtMost', symbol: '\u2264' },
];

export const LEVERAGE_OPTIONS = [
  { value: 'Low', label: 'Low', description: 'Garbage time (blowouts)' },
  { value: 'Medium', label: 'Medium', description: 'Normal game situations' },
  { value: 'High', label: 'High', description: 'Close game, late game' },
  { value: 'VeryHigh', label: 'Very High', description: 'Clutch time' },
];

export const PERIOD_OPTIONS = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: 5, label: 'OT' },
];

/** 30 NBA teams with PBPStats IDs */
export const NBA_TEAMS = [
  { id: '1610612737', name: 'Atlanta Hawks', abbreviation: 'ATL', color: '#E03A3E' },
  { id: '1610612738', name: 'Boston Celtics', abbreviation: 'BOS', color: '#007A33' },
  { id: '1610612751', name: 'Brooklyn Nets', abbreviation: 'BKN', color: '#000000' },
  { id: '1610612766', name: 'Charlotte Hornets', abbreviation: 'CHA', color: '#1D1160' },
  { id: '1610612741', name: 'Chicago Bulls', abbreviation: 'CHI', color: '#CE1141' },
  { id: '1610612739', name: 'Cleveland Cavaliers', abbreviation: 'CLE', color: '#6F263D' },
  { id: '1610612742', name: 'Dallas Mavericks', abbreviation: 'DAL', color: '#00538C' },
  { id: '1610612743', name: 'Denver Nuggets', abbreviation: 'DEN', color: '#0E2240' },
  { id: '1610612765', name: 'Detroit Pistons', abbreviation: 'DET', color: '#C8102E' },
  { id: '1610612744', name: 'Golden State Warriors', abbreviation: 'GSW', color: '#1D428A' },
  { id: '1610612745', name: 'Houston Rockets', abbreviation: 'HOU', color: '#CE1141' },
  { id: '1610612754', name: 'Indiana Pacers', abbreviation: 'IND', color: '#002D62' },
  { id: '1610612746', name: 'LA Clippers', abbreviation: 'LAC', color: '#C8102E' },
  { id: '1610612747', name: 'Los Angeles Lakers', abbreviation: 'LAL', color: '#552583' },
  { id: '1610612763', name: 'Memphis Grizzlies', abbreviation: 'MEM', color: '#5D76A9' },
  { id: '1610612748', name: 'Miami Heat', abbreviation: 'MIA', color: '#98002E' },
  { id: '1610612749', name: 'Milwaukee Bucks', abbreviation: 'MIL', color: '#00471B' },
  { id: '1610612750', name: 'Minnesota Timberwolves', abbreviation: 'MIN', color: '#0C2340' },
  { id: '1610612740', name: 'New Orleans Pelicans', abbreviation: 'NOP', color: '#0C2340' },
  { id: '1610612752', name: 'New York Knicks', abbreviation: 'NYK', color: '#006BB6' },
  { id: '1610612760', name: 'Oklahoma City Thunder', abbreviation: 'OKC', color: '#007AC1' },
  { id: '1610612753', name: 'Orlando Magic', abbreviation: 'ORL', color: '#0077C0' },
  { id: '1610612755', name: 'Philadelphia 76ers', abbreviation: 'PHI', color: '#006BB6' },
  { id: '1610612756', name: 'Phoenix Suns', abbreviation: 'PHX', color: '#1D1160' },
  { id: '1610612757', name: 'Portland Trail Blazers', abbreviation: 'POR', color: '#E03A3E' },
  { id: '1610612758', name: 'Sacramento Kings', abbreviation: 'SAC', color: '#5A2D81' },
  { id: '1610612759', name: 'San Antonio Spurs', abbreviation: 'SAS', color: '#C4CED4' },
  { id: '1610612761', name: 'Toronto Raptors', abbreviation: 'TOR', color: '#CE1141' },
  { id: '1610612762', name: 'Utah Jazz', abbreviation: 'UTA', color: '#002B5C' },
  { id: '1610612764', name: 'Washington Wizards', abbreviation: 'WAS', color: '#002B5C' },
];

// ─── Column Definitions ──────────────────────────────────────────────────────

export interface ColumnDef {
  key: string;
  label: string;
  format: (v: number | null | undefined) => string;
  formatRate?: (v: number | null | undefined) => string;
  group: string;
  isPer100?: boolean;
  isCalculated?: boolean;
  isPct?: boolean;
  isCountingStat?: boolean;
  possType?: 'off' | 'def';
}

export interface AggSummaryItem {
  key: string;
  label: string;
  format: (agg: NbaRatesAggregateStats) => string;
  valueColor?: (agg: NbaRatesAggregateStats) => string;
  bold?: boolean;
}

/** Default silhouette avatar for players without headshots */
export const DEFAULT_AVATAR = 'https://awesemostorage.blob.core.windows.net/assets/silhouette.png';

/** Name suffixes to handle when extracting last names */
const NAME_SUFFIXES = ['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v'];

/** Extract last name from full name, handling suffixes like Jr., III, etc. */
export const getLastName = (fullName: string): string => {
  const parts = fullName.split(' ').filter(Boolean);
  if (parts.length <= 1) return fullName;
  const lastPart = parts[parts.length - 1].toLowerCase();
  if (NAME_SUFFIXES.includes(lastPart) && parts.length > 2) {
    return parts[parts.length - 2];
  }
  return parts[parts.length - 1];
};
